import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useHistory, useLocation } from "react-router-dom";

import { prototypes } from "../config/prototypes";
import { useSlateTableRegistry } from "../contexts/SlateTableRegistryContext";
import { useSearchParams } from "../hooks/useSearchParams";

// Top of the dev-tools z-index hierarchy. We use one below the 32-bit signed
// max so prototypes can never accidentally outrank the picker by reaching for
// a "really big" zIndex (commonly 9999 / 10000). Paired with a portal to
// document.body in the floating (non-inline) case so no parent stacking
// context can trap the picker. Inline mode intentionally renders in-tree and
// keeps a normal z-index since callers position it themselves.
const DEV_TOOL_Z_INDEX = 2147483646;

// Settings icon component matching Figma design (Slate Icons - FS Settings)
function SettingsIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        color: "currentColor",
        display: "block",
        transform: "translateY(0.5px)",
      }}
    >
      {/* Gear icon with 6 rounded teeth - matching Figma design */}
      <path
        d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M19.43 12.97C19.47 12.66 19.5 12.34 19.5 12C19.5 11.66 19.47 11.34 19.43 11.03L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.65 15.5 5.32 14.87 5.07L14.49 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.51 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11.03C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.51 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.49 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

interface ConceptInfo {
  /** The underlying variant number this concept maps to */
  variantNumber: number;
  label: string;
  lastUpdated?: string;
  /** Optional group name - concepts with same group are nested under a collapsible header */
  group?: string;
}

/** When provided, renders one bar with multiple dropdown segments (e.g. PDP Refresh | PDP layout | Reviews). */
export interface VariantSwitcherSegment {
  featureTitle: string;
  concepts: ConceptInfo[];
  activeVariant: number;
  onVariantChange: (variant: number) => void;
  variantParamName?: string;
  syncVariantToUrl?: boolean;
}

interface VariantSwitcherProps {
  /** Required in single-segment mode; ignored when segments is provided. */
  activeVariant?: number;
  /** Required in single-segment mode; ignored when segments is provided. */
  onVariantChange?: (variant: number) => void;
  /** Concepts in display order (e.g., most recent first). Omit or pass [] when hideConceptSelector. Ignored when segments is provided. */
  concepts?: ConceptInfo[];
  /** Feature name to display. Ignored when segments is provided. */
  featureTitle?: string;
  /** When set, renders one bar with multiple segments (title + dropdown each). Single-concept props are ignored. */
  segments?: VariantSwitcherSegment[];
  /** If true, hide the concept dropdown and show only extraControls (e.g. controls-only prototypes). */
  hideConceptSelector?: boolean;
  /** If true, renders inline instead of fixed bottom-left */
  inline?: boolean;
  /** If true, pins the floating bar to the top-left (default is bottom-left). */
  floatTop?: boolean;
  /** If true, pins the floating bar to the right edge, vertically centered. Takes precedence over floatTop. */
  floatRight?: boolean;
  /** Visual navigation toggle props */
  showVisualNav?: boolean;
  onVisualNavChange?: (show: boolean) => void;
  /** Settings menu options */
  settingsOptions?: ConceptInfo[];
  /** Active settings option */
  activeSettings?: number;
  /** Settings change handler */
  onSettingsChange?: (option: number) => void;
  /** Optional footer content rendered at bottom of settings menu */
  settingsFooter?: React.ReactNode;
  /** Optional extra controls rendered in the bar (e.g. title size for concepts 1/1b) */
  extraControls?: React.ReactNode;
  /** URL query param name for variant sync (default 'variant'). Use distinct names when multiple switchers share a page. */
  variantParamName?: string;
  /** When false, variant is not read from or written to URL (always use in-page state; good for panel/compass so it doesn't disappear on refresh). Default true. */
  syncVariantToUrl?: boolean;
  /** When multiple switchers are on the same page, pass 0, 1, 2, ... to stack them vertically and avoid overlap. */
  stackIndex?: number;
  /** If true, flyout menus open downward instead of upward. Useful when the switcher is at the top of the page. */
  dropDown?: boolean;
  /** If true, the bar starts expanded on mount. */
  defaultOpen?: boolean;
  /** If true, the bar stays expanded and never minimizes on scroll. */
  alwaysExpanded?: boolean;
  /** If true, the bar can be dragged to reposition. */
  draggable?: boolean;
  /** When set, replaces the concept label on the variant dropdown trigger (e.g. no active variant on a hub page). */
  variantTriggerLabelOverride?: string;
}

/**
 * A compact floating card for switching between design concepts.
 */
export function VariantSwitcher({
  activeVariant = 0,
  onVariantChange = () => {},
  concepts = [],
  featureTitle,
  hideConceptSelector = false,
  inline = false,
  floatTop = false,
  floatRight = false,
  showVisualNav,
  onVisualNavChange,
  settingsOptions = [],
  activeSettings,
  onSettingsChange,
  settingsFooter,
  extraControls,
  variantParamName = "variant",
  syncVariantToUrl = true,
  stackIndex,
  segments,
  dropDown = false,
  defaultOpen = false,
  alwaysExpanded = false,
  draggable: isDraggable = false,
  variantTriggerLabelOverride,
}: VariantSwitcherProps) {
  const menusOpenDownward = dropDown || floatTop;
  const useSegments = segments != null && segments.length > 0;
  const tableRegistry = useSlateTableRegistry();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [openSegmentIndex, setOpenSegmentIndex] = useState<number | null>(null);
  const [isVisualNavOpen, setIsVisualNavOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const conceptFlyoutRef = useRef<HTMLDivElement>(null);
  const segmentTriggerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const segmentFlyoutRef = useRef<HTMLDivElement>(null);
  const [conceptDropdownPosition, setConceptDropdownPosition] = useState<{
    left: number;
    bottom?: number;
    top?: number;
  } | null>(null);
  const [segmentDropdownPosition, setSegmentDropdownPosition] = useState<{
    left: number;
    bottom?: number;
    top?: number;
  } | null>(null);
  const visualNavDropdownRef = useRef<HTMLDivElement>(null);
  const visualNavFlyoutRef = useRef<HTMLDivElement>(null);
  const [visualNavDropdownPosition, setVisualNavDropdownPosition] = useState<{
    left: number;
    bottom?: number;
    top?: number;
  } | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsFlyoutRef = useRef<HTMLDivElement>(null);
  const [settingsDropdownPosition, setSettingsDropdownPosition] = useState<{
    left: number;
    bottom?: number;
    top?: number;
  } | null>(null);
  const history = useHistory();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPrototype = prototypes.find(
    (p) => p.path && p.path === location.pathname
  );
  const figmaLink = currentPrototype?.figmaLink;

  // Drag state
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(
    () => {
      if (!isDraggable) return null;
      try {
        const saved = localStorage.getItem("variant-switcher-pos");
        return saved ? JSON.parse(saved) : null;
      } catch {
        return null;
      }
    }
  );
  const dragState = useRef({
    active: false,
    didDrag: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  useEffect(() => {
    if (!isDraggable) return;
    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.current.active) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        dragState.current.didDrag = true;
      }
      setDragPos({
        x: e.clientX - dragState.current.offsetX,
        y: e.clientY - dragState.current.offsetY,
      });
    };
    const onMouseUp = () => {
      if (dragState.current.active && dragState.current.didDrag) {
        setDragPos((prev) => {
          if (prev) {
            try {
              localStorage.setItem(
                "variant-switcher-pos",
                JSON.stringify(prev)
              );
            } catch {}
          }
          return prev;
        });
      }
      dragState.current.active = false;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDraggable]);

  const handleDragMouseDown = () => {};

  const handleToggleMouseDown = (e: React.MouseEvent) => {
    if (!isDraggable) return;
    e.preventDefault();
    dragState.current.active = true;
    dragState.current.didDrag = false;
    dragState.current.startX = e.clientX;
    dragState.current.startY = e.clientY;
    const rect = containerRef.current!.getBoundingClientRect();
    dragState.current.offsetX = e.clientX - rect.left;
    dragState.current.offsetY = e.clientY - rect.top;
  };

  // Minimize when user scrolls (smooth transition); small threshold so we don't minimize on load
  useEffect(() => {
    if (inline || alwaysExpanded) return;
    const SCROLL_THRESHOLD = 40;
    const handleScroll = () => {
      if (window.scrollY > SCROLL_THRESHOLD) setIsMinimized(true);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [inline, alwaysExpanded]);

  // URL sync: Read variant from URL on mount and sync to parent state (skipped when syncVariantToUrl is false)
  useEffect(() => {
    if (useSegments) {
      segments.forEach((seg, i) => {
        if (!seg.syncVariantToUrl) return;
        const paramName = seg.variantParamName ?? "variant";
        const urlVariant = searchParams.get(paramName);
        if (urlVariant) {
          const parsed = parseInt(urlVariant, 10);
          if (
            !isNaN(parsed) &&
            parsed !== seg.activeVariant &&
            seg.concepts.some((c) => c.variantNumber === parsed)
          ) {
            seg.onVariantChange(parsed);
          }
        }
      });
      return;
    }
    if (!syncVariantToUrl) return;
    const urlVariant = searchParams.get(variantParamName);
    if (urlVariant) {
      const parsed = parseInt(urlVariant, 10);
      // Only sync if it's a valid variant different from current
      if (
        !isNaN(parsed) &&
        parsed !== activeVariant &&
        concepts.some((c) => c.variantNumber === parsed)
      ) {
        onVariantChange(parsed);
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL sync: Update URL when variant changes (skip when concept selector is hidden or sync disabled)
  useEffect(() => {
    if (useSegments) {
      segments.forEach((seg) => {
        if (!seg.syncVariantToUrl) return;
        const paramName = seg.variantParamName ?? "variant";
        const currentUrlVariant = searchParams.get(paramName);
        const currentVariantStr = String(seg.activeVariant);
        if (currentUrlVariant !== currentVariantStr) {
          setSearchParams(
            (prev) => {
              prev.set(paramName, currentVariantStr);
              return prev;
            },
            { replace: true }
          );
        }
      });
      return;
    }
    if (!syncVariantToUrl || hideConceptSelector) return;
    const currentUrlVariant = searchParams.get(variantParamName);
    const currentVariantStr = String(activeVariant);
    // Only update if different to avoid infinite loops
    if (currentUrlVariant !== currentVariantStr) {
      setSearchParams(
        (prev) => {
          prev.set(variantParamName, currentVariantStr);
          return prev;
        },
        { replace: true }
      );
    }
  }, [
    useSegments,
    activeVariant,
    hideConceptSelector,
    searchParams,
    setSearchParams,
    variantParamName,
    syncVariantToUrl,
    segments,
  ]);

  // Toggle group expansion
  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  // Organize concepts into ungrouped items and groups
  const ungroupedConcepts = concepts.filter((c) => !c.group);
  const groupedConcepts = concepts.filter((c) => c.group);
  const groups = Array.from(new Set(groupedConcepts.map((c) => c.group!)));
  const conceptsByGroup: Record<string, ConceptInfo[]> = groups.reduce(
    (acc, group) => {
      acc[group] = groupedConcepts.filter((c) => c.group === group);
      return acc;
    },
    {} as Record<string, ConceptInfo[]>
  );

  // Position concept dropdown via portal (so it isn't clipped by overflow-hidden)
  useLayoutEffect(() => {
    if (!isOpen) {
      setConceptDropdownPosition(null);
      return;
    }
    const el = dropdownRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setConceptDropdownPosition({
      left: rect.left,
      ...(menusOpenDownward
        ? { top: rect.bottom + 8 }
        : { bottom: window.innerHeight - rect.top + 8 }),
    });
  }, [isOpen, menusOpenDownward]);

  // Position segment dropdown when a segment is open
  useLayoutEffect(() => {
    if (openSegmentIndex == null || !useSegments) {
      setSegmentDropdownPosition(null);
      return;
    }
    const el = segmentTriggerRefs.current[openSegmentIndex];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setSegmentDropdownPosition({
      left: rect.left,
      ...(menusOpenDownward
        ? { top: rect.bottom + 8 }
        : { bottom: window.innerHeight - rect.top + 8 }),
    });
  }, [openSegmentIndex, useSegments, menusOpenDownward]);

  // Position visual nav dropdown via portal (so it isn't clipped by overflow-hidden)
  useLayoutEffect(() => {
    if (!isVisualNavOpen) {
      setVisualNavDropdownPosition(null);
      return;
    }
    const el = visualNavDropdownRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setVisualNavDropdownPosition({
      left: rect.left,
      ...(menusOpenDownward
        ? { top: rect.bottom + 8 }
        : { bottom: window.innerHeight - rect.top + 8 }),
    });
  }, [isVisualNavOpen, menusOpenDownward]);

  // Position settings dropdown via portal (so it isn't clipped by overflow-hidden)
  useLayoutEffect(() => {
    if (!isSettingsOpen) {
      setSettingsDropdownPosition(null);
      return;
    }
    const el = settingsRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setSettingsDropdownPosition({
      left: rect.left,
      ...(menusOpenDownward
        ? { top: rect.bottom + 8 }
        : { bottom: window.innerHeight - rect.top + 8 }),
    });
  }, [isSettingsOpen, menusOpenDownward]);

  // Close dropdown when clicking outside (portaled flyouts live in document.body, so check their refs too)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inFlyout = conceptFlyoutRef.current?.contains(target);
      if (!inTrigger && !inFlyout) {
        setIsOpen(false);
      }
      const inSegmentTrigger = segmentTriggerRefs.current.some((ref) =>
        ref?.contains(target)
      );
      const inSegmentFlyout = segmentFlyoutRef.current?.contains(target);
      if (!inSegmentTrigger && !inSegmentFlyout) {
        setOpenSegmentIndex(null);
      }
      const inVisualNavTrigger = visualNavDropdownRef.current?.contains(target);
      const inVisualNavFlyout = visualNavFlyoutRef.current?.contains(target);
      if (!inVisualNavTrigger && !inVisualNavFlyout) {
        setIsVisualNavOpen(false);
      }
      const inSettingsTrigger = settingsRef.current?.contains(target);
      const inSettingsFlyout = settingsFlyoutRef.current?.contains(target);
      if (!inSettingsTrigger && !inSettingsFlyout) {
        setIsSettingsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeConcept = concepts.find((c) => c.variantNumber === activeVariant);

  const fontStyle = {
    fontFamily:
      '"Source Code Pro", ui-monospace, Menlo, Monaco, "Courier New", monospace',
  };

  // Divider component for consistency
  const Divider = () => (
    <div className="h-4 w-px shrink-0 bg-[#4B5563]" aria-hidden />
  );

  const fixedPositionStyle =
    !inline && stackIndex !== undefined
      ? { left: "1rem", bottom: `calc(1rem + ${stackIndex} * 3.5rem)` }
      : undefined;

  const dragPositionStyle =
    isDraggable && dragPos
      ? { left: dragPos.x, top: dragPos.y, bottom: "auto" as const }
      : undefined;

  const rootElement = (
    <div
      data-faire-dev-tool="variant-switcher"
      ref={containerRef}
      onMouseDown={handleDragMouseDown}
      className={
        inline
          ? "relative z-[9999] inline-block"
          : `fixed ${
              isDraggable && dragPos ? "" : floatRight ? "right-4" : "left-4"
            }`
      }
      style={{
        ...(inline
          ? undefined
          : {
              zIndex: DEV_TOOL_Z_INDEX,
              ...(dragPositionStyle ??
                fixedPositionStyle ??
                (floatRight
                  ? {
                      top: "50%",
                      bottom: "auto",
                      transform: "translateY(-50%)",
                    }
                  : floatTop
                    ? { top: "1rem", bottom: "auto" }
                    : { bottom: "1rem" })),
            }),
        ...(isDraggable ? { userSelect: "none" as const } : {}),
      }}
    >
      {/* Single morphing container: circle when minimized, pill when expanded */}
      <div
        className={`flex h-10 items-center rounded-full transition-shadow duration-300 ${
          isMinimized
            ? "shadow-[0_6px_24px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.25)]"
            : "shadow-[0_6px_24px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.1)]"
        }`}
        style={{
          background: "linear-gradient(180deg, #3C3C3C 0%, #2C2C2C 100%)",
        }}
      >
        {/* Toggle icon - sliders when minimized, X when expanded. Also drag handle when draggable. */}
        <button
          onClick={() => {
            if (isDraggable && dragState.current.didDrag) return;
            setIsMinimized(!isMinimized);
          }}
          onMouseDown={isDraggable ? handleToggleMouseDown : undefined}
          className={`flex h-10 w-10 shrink-0 items-center justify-center transition-colors ${
            isMinimized
              ? "text-fs-text-primary-inverse hover:text-[#d1d5dc]"
              : "hover:text-fs-text-primary-inverse text-[#9CA3AF]"
          }`}
          style={isDraggable ? { cursor: "grab" } : undefined}
          aria-label={isMinimized ? "Expand controls" : "Minimize controls"}
        >
          {isMinimized ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="4" y1="8" x2="20" y2="8" />
              <line x1="4" y1="16" x2="20" y2="16" />
              <line x1="9" y1="5" x2="9" y2="11" />
              <line x1="15" y1="13" x2="15" y2="19" />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          )}
        </button>

        {/* Expandable content area */}
        <div
          className={`flex min-w-0 items-center gap-2.5 overflow-x-auto overflow-y-hidden whitespace-nowrap transition-all duration-500 ${
            isMinimized
              ? "max-w-0 pr-0 opacity-0"
              : "max-w-[min(1280px,calc(100vw-6rem))] pr-3 opacity-100 [scrollbar-width:thin]"
          }`}
          style={{
            transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Divider />

          {/* Home button */}
          <button
            onClick={() => history.push("/")}
            className="hover:text-fs-text-primary-inverse shrink-0 text-[#9CA3AF] transition-colors"
            title="Back to home"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>

          <Divider />

          {/* Optional Figma link (no trailing divider in segment mode so there's only 1 divider between home and prototype name) */}
          {figmaLink && (
            <>
              <a
                href={figmaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-fs-text-primary-inverse shrink-0 text-[#9CA3AF] transition-colors"
                title="Open in Figma"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.99549 4.02414C2.39619 3.63363 2 2.95754 2 2.18889C2 0.98 2.98 0 4.18889 0H7.93916C9.14805 0 10.128 0.98 10.128 2.18889C10.128 2.95754 9.73188 3.63363 9.13256 4.02414C9.73188 4.41465 10.128 5.09076 10.128 5.85939C10.128 7.06828 9.14805 8.04828 7.93916 8.04828H7.89926C7.32781 8.04828 6.8075 7.8293 6.41768 7.47066V9.5099C6.41768 10.7323 5.41623 11.7188 4.19881 11.7188C2.9924 11.7188 2 10.7412 2 9.52986C2 8.76123 2.39617 8.08514 2.99545 7.69463C2.39617 7.30412 2 6.62803 2 5.85939C2 5.09076 2.39619 4.41465 2.99549 4.02414ZM6.41768 5.85939C6.41768 6.67766 7.08102 7.34098 7.89926 7.34098H7.93916C8.75742 7.34098 9.42074 6.67766 9.42074 5.85939C9.42074 5.04113 8.75742 4.37781 7.93916 4.37781H7.89926C7.08102 4.37781 6.41768 5.04113 6.41768 5.85939ZM5.71037 4.37781H4.18889C3.37063 4.37781 2.7073 5.04113 2.7073 5.85939C2.7073 6.676 3.36795 7.33828 4.18391 7.34098H4.18623H5.71037V4.37781ZM4.18889 8.04828C4.18723 8.04828 4.18557 8.04828 4.18391 8.04828C3.36795 8.05096 2.7073 8.71326 2.7073 9.52986C2.7073 10.3456 3.37807 11.0114 4.19881 11.0114C5.03057 11.0114 5.71037 10.3367 5.71037 9.5099V8.04828H4.18889ZM5.71037 3.67047H4.18889C3.37063 3.67047 2.7073 3.00715 2.7073 2.18889C2.7073 1.37063 3.37063 0.707304 4.18889 0.707304H5.71037V3.67047ZM7.93916 3.67047H6.41768V0.707304H7.93916C8.75742 0.707304 9.42074 1.37063 9.42074 2.18889C9.42074 3.00715 8.75742 3.67047 7.93916 3.67047Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
              {!useSegments && <Divider />}
            </>
          )}

          {/* Multi-segment mode: optional leading title (e.g. "PDP refresh") then title + dropdown per segment; no extra divider before name so only 1 between home and name */}
          {useSegments && (
            <>
              {featureTitle && (
                <span className="text-fs-text-primary-inverse text-[13px] font-medium">
                  {featureTitle}
                </span>
              )}
              {segments.map((seg, i) => {
                const activeConcept = seg.concepts.find(
                  (c) => c.variantNumber === seg.activeVariant
                );
                const isSegmentOpen = openSegmentIndex === i;
                return (
                  <React.Fragment key={i}>
                    <Divider />
                    <span className="text-fs-text-primary-inverse text-[13px] font-normal">
                      {seg.featureTitle}
                    </span>
                    <div
                      className="relative"
                      ref={(el) => {
                        segmentTriggerRefs.current[i] = el;
                      }}
                    >
                      <button
                        onClick={() =>
                          setOpenSegmentIndex(isSegmentOpen ? null : i)
                        }
                        className={`flex items-center gap-1 text-[13px] transition-colors ${
                          isSegmentOpen
                            ? "text-[#60A5FA]"
                            : "hover:text-fs-text-primary-inverse text-[#9CA3AF]"
                        }`}
                      >
                        <span>{activeConcept?.label ?? "Variant"}</span>
                        <svg
                          className={`h-2.5 w-2.5 transition-transform ${isSegmentOpen ? "rotate-180" : ""}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {isSegmentOpen &&
                        segmentDropdownPosition &&
                        ReactDOM.createPortal(
                          <div
                            ref={segmentFlyoutRef}
                            className="fixed z-[99999] max-h-[320px] overflow-y-auto rounded-lg border border-[#374151] bg-[#1F2937] shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
                            style={{
                              left: segmentDropdownPosition.left,
                              ...(segmentDropdownPosition.top != null
                                ? { top: segmentDropdownPosition.top }
                                : { bottom: segmentDropdownPosition.bottom }),
                            }}
                          >
                            <div className="py-1">
                              {seg.concepts
                                .filter((c) => !c.group)
                                .map((concept) => (
                                  <button
                                    key={concept.variantNumber}
                                    onClick={() => {
                                      seg.onVariantChange(
                                        concept.variantNumber
                                      );
                                      setOpenSegmentIndex(null);
                                    }}
                                    className={`flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left whitespace-nowrap transition-colors ${
                                      seg.activeVariant ===
                                      concept.variantNumber
                                        ? "bg-[#374151]"
                                        : "hover:bg-[#374151]/50"
                                    }`}
                                  >
                                    <span
                                      className={`text-xs ${seg.activeVariant === concept.variantNumber ? "text-fs-text-primary-inverse font-medium" : "text-[#D1D5DB]"}`}
                                    >
                                      {concept.label}
                                    </span>
                                  </button>
                                ))}
                            </div>
                          </div>,
                          document.body
                        )}
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}

          {/* Single-segment: Feature title / Prototype name + concept selector */}
          {!useSegments && (
            <>
              <span className="text-fs-text-primary-inverse text-[13px] font-medium">
                {featureTitle || "Prototype"}
              </span>
              <Divider />
            </>
          )}

          {/* Concept selector (Variant dropdown) - single-segment only */}
          {!useSegments && !hideConceptSelector && (
            <>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className={`flex items-center gap-1 text-[13px] transition-colors ${
                    isOpen
                      ? "text-[#60A5FA]"
                      : "hover:text-fs-text-primary-inverse text-[#9CA3AF]"
                  }`}
                >
                  <span>
                    {variantTriggerLabelOverride ??
                      activeConcept?.label ??
                      "Variant"}
                  </span>
                  <svg
                    className={`h-2.5 w-2.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Flyout menu in portal */}
                {isOpen &&
                  conceptDropdownPosition &&
                  ReactDOM.createPortal(
                    <div
                      ref={conceptFlyoutRef}
                      className="fixed z-[99999] max-h-[320px] overflow-y-auto rounded-lg border border-[#374151] bg-[#1F2937] shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
                      style={{
                        left: conceptDropdownPosition.left,
                        ...(conceptDropdownPosition.top != null
                          ? { top: conceptDropdownPosition.top }
                          : { bottom: conceptDropdownPosition.bottom }),
                      }}
                    >
                      <div className="py-1">
                        {ungroupedConcepts.map((concept) => (
                          <button
                            key={concept.variantNumber}
                            onClick={() => {
                              onVariantChange(concept.variantNumber);
                              setIsOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left whitespace-nowrap transition-colors ${
                              activeVariant === concept.variantNumber
                                ? "bg-[#374151]"
                                : "hover:bg-[#374151]/50"
                            }`}
                          >
                            <span
                              className={`text-xs ${activeVariant === concept.variantNumber ? "text-fs-text-primary-inverse font-medium" : "text-[#D1D5DB]"}`}
                            >
                              {concept.label}
                            </span>
                            {concept.lastUpdated && (
                              <span className="text-xs text-[#6B7280]">
                                {concept.lastUpdated}
                              </span>
                            )}
                          </button>
                        ))}
                        {groups.map((group) => {
                          const isGroupExpanded = expandedGroups.has(group);
                          const groupConcepts = conceptsByGroup[group];
                          const hasActiveInGroup = groupConcepts.some(
                            (c) => c.variantNumber === activeVariant
                          );
                          return (
                            <div key={group}>
                              {ungroupedConcepts.length > 0 && (
                                <div className="mx-2 my-1 border-t border-[#374151]" />
                              )}
                              <button
                                onClick={() => toggleGroup(group)}
                                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left whitespace-nowrap transition-colors hover:bg-[#374151]/50 ${hasActiveInGroup ? "bg-[#374151]/30" : ""}`}
                              >
                                <svg
                                  className={`h-3 w-3 text-[#6B7280] transition-transform ${isGroupExpanded ? "rotate-90" : ""}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span className="text-xs text-[#9CA3AF]">
                                  {group}
                                </span>
                                <span className="text-[10px] text-[#6B7280]">
                                  ({groupConcepts.length})
                                </span>
                              </button>
                              {isGroupExpanded &&
                                groupConcepts.map((concept) => (
                                  <button
                                    key={concept.variantNumber}
                                    onClick={() => {
                                      onVariantChange(concept.variantNumber);
                                      setIsOpen(false);
                                    }}
                                    className={`flex w-full items-center justify-between gap-4 py-1.5 pr-3 pl-7 text-left whitespace-nowrap transition-colors ${activeVariant === concept.variantNumber ? "bg-[#374151]" : "hover:bg-[#374151]/50"}`}
                                  >
                                    <span
                                      className={`text-xs ${activeVariant === concept.variantNumber ? "text-fs-text-primary-inverse font-medium" : "text-[#D1D5DB]"}`}
                                    >
                                      {concept.label}
                                    </span>
                                    {concept.lastUpdated && (
                                      <span className="text-xs text-[#6B7280]">
                                        {concept.lastUpdated}
                                      </span>
                                    )}
                                  </button>
                                ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>,
                    document.body
                  )}
              </div>
            </>
          )}

          {/* Extra controls */}
          {extraControls != null && (
            <>
              <Divider />
              {extraControls}
            </>
          )}

          {/* Table config toggles (auto-detected from SlateTableWithConfig instances) */}
          {tableRegistry &&
            tableRegistry.tables.length > 0 &&
            tableRegistry.tables.map((table, i) => (
              <React.Fragment key={table.id}>
                {(i > 0 || !hideConceptSelector || extraControls != null) && (
                  <Divider />
                )}
                <button
                  onClick={() => {
                    table.onToggleConfig();
                    table.onScrollTo();
                  }}
                  className={`flex items-center gap-1.5 text-[13px] transition-colors ${
                    tableRegistry?.activeConfigId === table.id
                      ? "text-[#60A5FA]"
                      : "hover:text-fs-text-primary-inverse text-[#9CA3AF]"
                  }`}
                  title={`Configure ${table.name}`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 256 256"
                    fill="currentColor"
                  >
                    <path d="M224,48H32A16,16,0,0,0,16,64V192a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V64A16,16,0,0,0,224,48Zm0,144H32V64H224V192ZM200,80H56a8,8,0,0,0-8,8v16a8,8,0,0,0,8,8H200a8,8,0,0,0,8-8V88A8,8,0,0,0,200,80Zm-8,16H64V96H192ZM96,128H56a8,8,0,0,0-8,8v32a8,8,0,0,0,8,8H96a8,8,0,0,0,8-8V136A8,8,0,0,0,96,128Zm-8,32H64V144H88Zm112-32H136a8,8,0,0,0-8,8v32a8,8,0,0,0,8,8h64a8,8,0,0,0,8-8V136A8,8,0,0,0,200,128Zm-8,32H144V144h48Z" />
                  </svg>
                  <span>{table.name}</span>
                </button>
              </React.Fragment>
            ))}

          {/* Visual Navigation Toggle */}
          {showVisualNav !== undefined && onVisualNavChange && (
            <>
              <Divider />
              <div className="relative" ref={visualNavDropdownRef}>
                <button
                  onClick={() => setIsVisualNavOpen(!isVisualNavOpen)}
                  className={`flex items-center gap-1 text-[13px] transition-colors ${
                    isVisualNavOpen
                      ? "text-[#60A5FA]"
                      : "hover:text-fs-text-primary-inverse text-[#9CA3AF]"
                  }`}
                >
                  <span>Nav</span>
                  <svg
                    className={`h-2.5 w-2.5 transition-transform ${isVisualNavOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isVisualNavOpen &&
                  visualNavDropdownPosition &&
                  ReactDOM.createPortal(
                    <div
                      ref={visualNavFlyoutRef}
                      className="fixed z-[99999] rounded-lg border border-[#374151] bg-[#1F2937] shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
                      style={{
                        left: visualNavDropdownPosition.left,
                        ...(visualNavDropdownPosition.top != null
                          ? { top: visualNavDropdownPosition.top }
                          : { bottom: visualNavDropdownPosition.bottom }),
                      }}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            onVisualNavChange(true);
                            setIsVisualNavOpen(false);
                          }}
                          className={`flex w-full items-center px-3 py-1.5 text-left whitespace-nowrap transition-colors ${showVisualNav ? "bg-[#374151]" : "hover:bg-[#374151]/50"}`}
                        >
                          <span
                            className={`text-xs ${showVisualNav ? "text-fs-text-primary-inverse font-medium" : "text-[#D1D5DB]"}`}
                          >
                            On
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            onVisualNavChange(false);
                            setIsVisualNavOpen(false);
                          }}
                          className={`flex w-full items-center px-3 py-1.5 text-left whitespace-nowrap transition-colors ${!showVisualNav ? "bg-[#374151]" : "hover:bg-[#374151]/50"}`}
                        >
                          <span
                            className={`text-xs ${!showVisualNav ? "text-fs-text-primary-inverse font-medium" : "text-[#D1D5DB]"}`}
                          >
                            Off
                          </span>
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
              </div>
            </>
          )}

          {/* Settings */}
          {settingsOptions.length > 0 && (
            <>
              <Divider />
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                  className={`hover:text-fs-text-primary-inverse text-[#9CA3AF] transition-colors ${isSettingsOpen ? "text-fs-text-primary-inverse" : ""}`}
                  title="Settings"
                >
                  <SettingsIcon />
                </button>
                {isSettingsOpen &&
                  settingsDropdownPosition &&
                  ReactDOM.createPortal(
                    <div
                      ref={settingsFlyoutRef}
                      className="fixed z-[99999] max-h-[320px] overflow-y-auto rounded-lg border border-[#374151] bg-[#1F2937] shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
                      style={{
                        left: settingsDropdownPosition.left,
                        ...(settingsDropdownPosition.top != null
                          ? { top: settingsDropdownPosition.top }
                          : { bottom: settingsDropdownPosition.bottom }),
                      }}
                    >
                      <div className="py-1">
                        {settingsOptions.map((option) => (
                          <button
                            key={option.variantNumber}
                            onClick={() => {
                              onSettingsChange?.(option.variantNumber);
                              setIsSettingsOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left whitespace-nowrap transition-colors ${activeSettings === option.variantNumber ? "bg-[#374151]" : "hover:bg-[#374151]/50"}`}
                          >
                            <span
                              className={`text-xs ${activeSettings === option.variantNumber ? "text-fs-text-primary-inverse font-medium" : "text-[#D1D5DB]"}`}
                            >
                              {option.label}
                            </span>
                          </button>
                        ))}
                      </div>
                      {settingsFooter && (
                        <div className="border-t border-[#374151] px-3 py-2">
                          {settingsFooter}
                        </div>
                      )}
                    </div>,
                    document.body
                  )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Floating mode portals to body so no parent stacking context can trap the
  // picker. Inline mode renders in place (callers position it themselves).
  return inline
    ? rootElement
    : ReactDOM.createPortal(rootElement, document.body);
}
